import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ui } from '@procuros/datachecks';
import { ColumnDef } from '@tanstack/react-table';

import { PartnerV2 } from 'support/types';
import { WebEDIRowData } from 'components/DataTable/Types';
import { Alert } from 'components/Display/Alert/Alert';
import { AlertSection } from 'components/Display/Alert/AlertSection';
import { ErrorGroup } from 'components/Display/UploadErrorModal/types';
import { UploadErrorModal } from 'components/Display/UploadErrorModal/UploadErrorModal';
import { Button } from 'components/Form/Button/Button';
import { Translate } from 'components/Translate/Translate';

import { FileUploadError, TemplateValidationErrorCodes, ValidationErrorDetail } from '../helpers';
import { formatErrorColumns, getColumnLabel, MAX_COLUMNS_TO_DISPLAY } from './columnFormatter';
import { getFieldFormatErrorMessage } from './errorMessageFormatter';

export type UploadErrorDisplayProps = {
  uploadError: FileUploadError;
  receiverPartner: PartnerV2;
  columns: Array<ColumnDef<WebEDIRowData, unknown>>;
  onDownloadTemplate: () => void;
};

export const PriceCatalogUploadErrorDisplay: FC<UploadErrorDisplayProps> = ({
  uploadError,
  receiverPartner,
  columns,
  onDownloadTemplate,
}) => {
  const { t } = useTranslation();
  const [errorDetailsModalOpen, setErrorDetailsModalOpen] = useState(false);
  const errorDetailsButtonRef = useRef<HTMLButtonElement>(null);

  const { columns: formattedColumns, lastColumn } = formatErrorColumns(uploadError, columns);
  const hasColumnsData = 'columns' in uploadError && uploadError.columns?.length;
  const hasTooManyColumns = hasColumnsData && uploadError.columns!.length > MAX_COLUMNS_TO_DISPLAY;
  const hasErrorDetails =
    'errorDetails' in uploadError && uploadError.errorDetails && uploadError.errorDetails.length > 0;

  const isTemplateValidationError = Object.values(TemplateValidationErrorCodes).includes(
    uploadError.code as TemplateValidationErrorCodes,
  );

  const errorCodeSuffix =
    isTemplateValidationError && hasTooManyColumns ? `${uploadError.code}_MANY_COLUMNS` : uploadError.code;

  const values = {
    partnerName: receiverPartner?.name || '',
    columns: formattedColumns,
    lastColumn,
    count: hasColumnsData ? uploadError.columns?.length : 0,
  };

  const errorGroups: Array<ErrorGroup> = useMemo(() => {
    if (!hasErrorDetails) {
      return [];
    }

    const errorDetails = 'errorDetails' in uploadError ? uploadError.errorDetails : [];
    if (!errorDetails) {
      return [];
    }

    const groups = new Map<string, Array<ValidationErrorDetail>>();
    errorDetails.forEach((error) => {
      const columnLabel = getColumnLabel(error.column, columns);
      if (!groups.has(columnLabel)) {
        groups.set(columnLabel, []);
      }
      groups.get(columnLabel)!.push(error);
    });

    return Array.from(groups.entries()).map(([columnLabel, errors]) => ({
      columnLabel,
      errorCount: errors.length,
      rows: errors
        .sort((a, b) => a.row - b.row)
        .map((error) => {
          const column = columns.find((col) => col.id === error.column);
          const options = column?.meta?.optionalConfig?.options;
          const formattedMessage = getFieldFormatErrorMessage({
            fieldType: error.expectedFormat as ui.FieldTypes,
            columnOptions: options,
            t,
          });

          return {
            row: error.row,
            columns: [{ value: String(error.row) }, { value: error.value }, { value: formattedMessage }],
          };
        }),
    }));
  }, [hasErrorDetails, uploadError, columns, t]);

  const columnHeaders = [
    { label: t('priceCatalogs:template.errorDetails.row'), className: 'w-20' },
    { label: t('priceCatalogs:template.errorDetails.currentValue'), className: 'w-48' },
    { label: t('priceCatalogs:template.errorDetails.expectedFormat') },
  ];

  useEffect(() => {
    if (hasErrorDetails && errorDetailsButtonRef.current) {
      errorDetailsButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      errorDetailsButtonRef.current.focus();
    }
  }, [hasErrorDetails]);

  return (
    <>
      <Alert severity="error" withoutIcon>
        <AlertSection severity="error" title={t(`priceCatalogs:template.uploadErrors.title.${uploadError.code}`)}>
          <Translate
            i18nKey={`priceCatalogs:template.uploadErrors.description.${errorCodeSuffix}`}
            values={values}
            components={{
              TemplateDownloader: (
                <button
                  className="m-0 inline cursor-pointer border-none bg-transparent p-0 underline hover:text-procuros-green-400"
                  onClick={onDownloadTemplate}
                />
              ),
            }}
          />
          {hasErrorDetails && (
            <div className="mt-4">
              <Button
                ref={errorDetailsButtonRef}
                variant="secondary"
                size="small"
                onClick={() => setErrorDetailsModalOpen(true)}
                analyticsId="price-catalog-upload-error-details-toggle"
              >
                {t('priceCatalogs:template.errorDetails.showDetails')}
              </Button>
            </div>
          )}
        </AlertSection>
      </Alert>
      {hasErrorDetails && (
        <UploadErrorModal
          open={errorDetailsModalOpen}
          onClose={() => setErrorDetailsModalOpen(false)}
          title={t('priceCatalogs:template.errorDetails.modalTitle')}
          description={t('priceCatalogs:template.errorDetails.modalDescription', {
            count: uploadError.errorDetails!.length,
          })}
          errorGroups={errorGroups}
          columnHeaders={columnHeaders}
        />
      )}
    </>
  );
};

/**
 * i18n
 * t('priceCatalogs:template.uploadErrors.title.file-invalid-type')
 * t('priceCatalogs:template.uploadErrors.title.INVALID_HEADERS')
 * t('priceCatalogs:template.uploadErrors.title.INVALID_DATA_FORMATS')
 * t('priceCatalogs:template.uploadErrors.title.UNKNOWN')
 *
 * t('priceCatalogs:template.uploadErrors.description.file-invalid-type')
 * t('priceCatalogs:template.uploadErrors.description.INVALID_HEADERS', {count: columns.length})
 * t('priceCatalogs:template.uploadErrors.description.INVALID_DATA_FORMATS', {count: columns.length})
 * t('priceCatalogs:template.uploadErrors.description.INVALID_HEADERS_MANY_COLUMNS')
 * t('priceCatalogs:template.uploadErrors.description.INVALID_DATA_FORMATS_MANY_COLUMNS')
 * t('priceCatalogs:template.uploadErrors.description.UNKNOWN')
 */
